import React, { useEffect, useState } from 'react';
import {
  AppBar, Box, Container, IconButton, Button, Stack, Tooltip, Avatar, Menu, MenuItem, Divider,
  Typography, Switch, FormControlLabel, ToggleButton, ToggleButtonGroup, useMediaQuery, useTheme, alpha, Popover,
} from '@mui/material';
import { NavLink, Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useScroll } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import AccessibilityNewRoundedIcon from '@mui/icons-material/AccessibilityNewRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import Logo from '../brand/Logo';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAppTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useSplash } from '../../contexts/SplashContext';

const NAV = [
  { key: 'home', to: '/' },
  { key: 'learn', to: '/learn' },
  { key: 'consultations', to: '/consultations' },
  { key: 'community', to: '/community' },
  { key: 'activities', to: '/activities' },
];

const Header: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { currentLanguage, changeLanguage } = useLanguage();
  const { mode, toggleThemeMode, accessibility, updateAccessibility } = useAppTheme();
  const { currentUser, userProfile, logout, isAdmin } = useAuth();
  const { done: splashDone } = useSplash();
  const navigate = useNavigate();
  const location = useLocation();

  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [userAnchor, setUserAnchor] = useState<null | HTMLElement>(null);
  const [a11yAnchor, setA11yAnchor] = useState<null | HTMLElement>(null);
  const { scrollY } = useScroll();

  useEffect(() => scrollY.on('change', (v) => setScrolled(v > 24)), [scrollY]);
  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const handleLogout = async () => {
    setUserAnchor(null);
    await logout();
    navigate('/');
  };

  const glass = {
    backdropFilter: 'blur(18px)',
    WebkitBackdropFilter: 'blur(18px)',
    backgroundColor: dark ? alpha(theme.palette.brand.paperDark, 0.7) : alpha('#fff', 0.72),
    border: `1px solid ${dark ? alpha('#fff', 0.08) : alpha('#fff', 0.9)}`,
    boxShadow: `0 10px 30px ${alpha(theme.palette.brand.navy, dark ? 0.35 : 0.08)}`,
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        color="transparent"
        sx={{ top: 0, pt: { xs: 1.5, md: 2 }, pb: 1, pointerEvents: 'none', backgroundImage: 'none' }}
      >
        <Container maxWidth="lg">
          <Box
            component={motion.div}
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
            sx={{
              pointerEvents: 'auto',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
              px: { xs: 1.5, md: 2 }, py: 1, borderRadius: 999,
              transition: 'all .35s ease',
              ...(scrolled || isMobile ? glass : { border: '1px solid transparent' }),
            }}
          >
            {/* Brand */}
            <Box component={RouterLink} to="/" aria-label={t('app.name')} sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
              {splashDone && <Logo height={34} layoutId="brand-logo" />}
            </Box>

            {/* Desktop nav */}
            {!isMobile && (
              <Stack direction="row" spacing={0.5} sx={{ position: 'relative' }}>
                {NAV.map((item) => (
                  <NavLink key={item.key} to={item.to} end={item.to === '/'} style={{ textDecoration: 'none' }}>
                    {({ isActive }) => (
                      <Box
                        sx={{
                          position: 'relative', px: 2, py: 1, borderRadius: 999, fontWeight: 700, fontSize: '0.95rem',
                          color: isActive ? (dark ? theme.palette.brand.ink : '#fff') : 'text.primary',
                          transition: 'color .3s',
                          '&:hover': { color: isActive ? undefined : theme.palette.primary.main },
                        }}
                      >
                        {isActive && (
                          <motion.span
                            layoutId="nav-active"
                            transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                            style={{
                              position: 'absolute', inset: 0, borderRadius: 999, zIndex: -1,
                              background: dark ? theme.palette.brand.lavender : theme.palette.brand.navy,
                            }}
                          />
                        )}
                        {t(`nav.${item.key}`)}
                      </Box>
                    )}
                  </NavLink>
                ))}
              </Stack>
            )}

            {/* Actions */}
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip title={t('common.language')} arrow>
                <Button
                  size="small"
                  onClick={() => changeLanguage(currentLanguage === 'ar' ? 'en' : 'ar')}
                  sx={{ minWidth: 0, px: 1.5, fontWeight: 800, color: 'text.primary' }}
                >
                  {currentLanguage === 'ar' ? 'EN' : 'ع'}
                </Button>
              </Tooltip>
              <Tooltip title={mode === 'dark' ? t('common.light_mode') : t('common.dark_mode')} arrow>
                <IconButton onClick={toggleThemeMode} size="small" sx={{ color: 'text.primary' }}>
                  <motion.span key={mode} initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} style={{ display: 'flex' }}>
                    {mode === 'dark' ? <LightModeRoundedIcon /> : <DarkModeRoundedIcon />}
                  </motion.span>
                </IconButton>
              </Tooltip>
              <Tooltip title={t('accessibility.title')} arrow>
                <IconButton onClick={(e) => setA11yAnchor(e.currentTarget)} size="small" sx={{ color: 'text.primary' }}>
                  <AccessibilityNewRoundedIcon />
                </IconButton>
              </Tooltip>

              {!isMobile && (
                currentUser ? (
                  <IconButton onClick={(e) => setUserAnchor(e.currentTarget)} size="small" sx={{ ml: 0.5 }}>
                    <Avatar
                      src={userProfile?.photoURL || undefined}
                      sx={{ width: 36, height: 36, bgcolor: theme.palette.brand.lavenderSoft, color: theme.palette.brand.navy }}
                    >
                      {(userProfile?.displayName || currentUser.email || '?').charAt(0).toUpperCase()}
                    </Avatar>
                  </IconButton>
                ) : (
                  <Stack direction="row" spacing={1} sx={{ ml: 1 }}>
                    <Button component={RouterLink} to="/login" variant="text" sx={{ color: 'text.primary' }}>
                      {t('nav.login')}
                    </Button>
                    <Button component={RouterLink} to="/signup" variant="gradient">
                      {t('nav.signup')}
                    </Button>
                  </Stack>
                )
              )}

              {isMobile && (
                <IconButton onClick={() => setOpen((o) => !o)} aria-label={open ? t('nav.close') : t('nav.menu')} sx={{ color: 'text.primary' }}>
                  {open ? <CloseRoundedIcon /> : <MenuRoundedIcon />}
                </IconButton>
              )}
            </Stack>
          </Box>
        </Container>
      </AppBar>

      {/* User menu */}
      <Menu
        anchorEl={userAnchor}
        open={Boolean(userAnchor)}
        onClose={() => setUserAnchor(null)}
        slotProps={{ paper: { sx: { mt: 1.5, minWidth: 220, borderRadius: 4, p: 0.5 } } }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" noWrap>{userProfile?.displayName || currentUser?.email}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap>{currentUser?.email}</Typography>
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem component={RouterLink} to="/profile" onClick={() => setUserAnchor(null)} sx={{ borderRadius: 2 }}>
          <PersonRoundedIcon fontSize="small" sx={{ mr: 1.5 }} /> {t('nav.profile')}
        </MenuItem>
        {isAdmin && (
          <MenuItem component={RouterLink} to="/admin" onClick={() => setUserAnchor(null)} sx={{ borderRadius: 2 }}>
            <AdminPanelSettingsRoundedIcon fontSize="small" sx={{ mr: 1.5 }} /> {t('nav.admin')}
          </MenuItem>
        )}
        <MenuItem onClick={handleLogout} sx={{ borderRadius: 2, color: 'error.main' }}>
          <LogoutRoundedIcon fontSize="small" sx={{ mr: 1.5 }} /> {t('nav.logout')}
        </MenuItem>
      </Menu>

      {/* Accessibility popover */}
      <Popover
        open={Boolean(a11yAnchor)}
        anchorEl={a11yAnchor}
        onClose={() => setA11yAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{ paper: { sx: { mt: 1.5, p: 2.5, width: 300, borderRadius: 4 } } }}
      >
        <Typography variant="h6">{t('accessibility.title')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{t('accessibility.subtitle')}</Typography>
        <FormControlLabel
          control={<Switch checked={accessibility.reduceAnimations} onChange={(e) => updateAccessibility({ reduceAnimations: e.target.checked })} />}
          label={t('accessibility.reduce_animations')}
        />
        <FormControlLabel
          control={<Switch checked={accessibility.highContrast} onChange={(e) => updateAccessibility({ highContrast: e.target.checked })} />}
          label={t('accessibility.high_contrast')}
        />
        <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 1 }}>{t('accessibility.font_size')}</Typography>
        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          value={accessibility.fontSize}
          onChange={(_, v) => v && updateAccessibility({ fontSize: v })}
          sx={{ '& .MuiToggleButton-root': { borderRadius: 999, textTransform: 'none', fontWeight: 700 } }}
        >
          <ToggleButton value="small">{t('accessibility.small')}</ToggleButton>
          <ToggleButton value="medium">{t('accessibility.medium')}</ToggleButton>
          <ToggleButton value="large">{t('accessibility.large')}</ToggleButton>
        </ToggleButtonGroup>
      </Popover>

      {/* Mobile full-screen menu */}
      <AnimatePresence>
        {open && isMobile && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0, clipPath: 'circle(0% at 92% 4%)' }}
            animate={{ opacity: 1, clipPath: 'circle(150% at 92% 4%)' }}
            exit={{ opacity: 0, clipPath: 'circle(0% at 92% 4%)' }}
            transition={{ duration: 0.55, ease: [0.76, 0, 0.24, 1] }}
            style={{
              position: 'fixed', inset: 0, zIndex: 1200, display: 'flex', flexDirection: 'column',
              justifyContent: 'center', padding: 32,
              background: dark ? theme.palette.brand.bgDark : theme.palette.brand.bgLight,
            }}
          >
            <Stack spacing={1.5} component="nav">
              {NAV.map((item, i) => (
                <motion.div key={item.key} initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 + i * 0.07 }}>
                  <NavLink to={item.to} end={item.to === '/'} style={{ textDecoration: 'none' }}>
                    {({ isActive }) => (
                      <Typography
                        variant="h3"
                        sx={{ color: isActive ? 'primary.main' : 'text.primary', fontWeight: 900 }}
                        className={isActive ? 'nd-gradient-text' : undefined}
                      >
                        {t(`nav.${item.key}`)}
                      </Typography>
                    )}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}>
                <Divider sx={{ my: 2 }} />
                {currentUser ? (
                  <Stack spacing={1.5}>
                    <Button component={RouterLink} to="/profile" variant="soft" size="large" startIcon={<PersonRoundedIcon />}>{t('nav.profile')}</Button>
                    {isAdmin && <Button component={RouterLink} to="/admin" variant="outlined" size="large">{t('nav.admin')}</Button>}
                    <Button onClick={handleLogout} variant="text" color="error" size="large">{t('nav.logout')}</Button>
                  </Stack>
                ) : (
                  <Stack spacing={1.5}>
                    <Button component={RouterLink} to="/signup" variant="gradient" size="large">{t('nav.signup')}</Button>
                    <Button component={RouterLink} to="/login" variant="soft" size="large">{t('nav.login')}</Button>
                  </Stack>
                )}
              </motion.div>
            </Stack>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
